/**
 * Concordância de gênero e número e listas (§13.4.5, §13.4.4 `joinList`). Funções puras; os modelos de texto do
 * catálogo (src/copy/strings.ts) as aplicam em `{palavra:X}` e `{VAR|singular|plural}`.
 */
import { fmtInt } from './numbers';

export type Gender = 'F' | 'M';

const WORDS = {
  eleito: ['eleito', 'eleita'],
  Eleito: ['Eleito', 'Eleita'],
  eleito_governador: ['eleito governador', 'eleita governadora'],
  nao_eleito: ['Não eleito', 'Não eleita'],
  favorito: ['favorito', 'favorita'],
  Favorito: ['Favorito', 'Favorita'],
  vencedor: ['vencedor', 'vencedora'],
} as const;
export type AgreeWord = keyof typeof WORDS;

export function isAgreeWord(word: string): word is AgreeWord {
  return Object.hasOwn(WORDS, word);
}

/** Gênero ausente (falha de cadastro) usa o masculino e avisa (§13.4.5). */
export function agree(g: Gender | null | undefined, word: AgreeWord): string {
  if (g !== 'F' && g !== 'M') console.warn('[app] gênero ausente', word);
  return WORDS[word][g === 'F' ? 1 : 0];
}

/** Feminino plural só se TODOS forem 'F'. */
export function agreeGroup(gs: readonly Gender[], word: AgreeWord): string {
  return WORDS[word][gs.length > 0 && gs.every((g) => g === 'F') ? 1 : 0];
}

/**
 * Singular só para n === 1; zero é plural ("1 seção" · "0 seções"). Em "{N} de {M} …" a palavra concorda com M; com
 * lista, o verbo concorda com a quantidade de sujeitos (`plural(cidades.length, 'traz', 'trazem')`).
 */
export function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm;
}

export interface JoinListOptions {
  /** Teto de itens exibidos: com mais de `max`, mostra os `max − 1` primeiros + "e mais {N}" (sobram 2 ou mais). */
  max?: number;
  /** Texto do resto depois de " e " (padrão "mais {N}", N por fmtInt). */
  more?: (n: number) => string;
}

const defaultMore = (n: number): string => `mais ${fmtInt(n)}`;

/**
 * 1 item → "A"; 2 → "A e B"; 3+ → "A, B e C" (sem vírgula antes do "e"); vazio → "". Com `max`: se sobra exatamente 1
 * item além dos `max − 1`, lista todos; senão "A, B, C e mais 3" (§13.4.4; `max` mínimo 2).
 */
export function joinList(items: readonly string[], opts: JoinListOptions = {}): string {
  if (opts.max !== undefined) {
    const max = Math.max(2, Math.floor(opts.max));
    if (items.length > max) {
      const keep = max - 1;
      return `${items.slice(0, keep).join(', ')} e ${(opts.more ?? defaultMore)(items.length - keep)}`;
    }
  }
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
}
